import { isNullOrUndefined } from '@intelligence/v3/utils/common';
import { getDateComparator } from '@intelligence/v3/utils/date';
import { SortType } from '@home/enums/common';
import { ALINEA_CALIBRATION_METRICS } from '@intelligence/v3/pages/TopCharts/PCConsoleGames/AlineaAnalyticsCalibration/constants';
import { getAlineaDateValue, getAlineaMetricItem } from '@intelligence/v3/pages/TopCharts/PCConsoleGames/AlineaAnalyticsCalibration/utils';

import type { TTrueDataMode } from '../types';

interface IMetricGroupFlags {
  is_metric_first: boolean;
  is_metric_last: boolean;
}

/**
 * 构建简单的排序 comparator
 * @description 支持数字、字符串、日期字符串的排序，处理 null/undefined 值
 * @date 2025-10-18
 * @author lv.chen
 */
export const buildSimpleComparator = (orderInfo: { orderMetric: string; order: string; }) => {
  const { orderMetric, order } = orderInfo;
  const isAsc = order === 'asc';
  const startDateComparator = getDateComparator(order as SortType);
  const endDateComparator = getDateComparator(SortType.Asc);

  if (orderMetric === 'metric_date') {
    return (a: any, b: any) => {
      // 先按 Metrics 分组，保证后续 markMetricGroups 能识别连续重复项；日期排序只影响组内顺序。
      const metricCompare = String(a?.metric_name || a?.metric || '').localeCompare(
        String(b?.metric_name || b?.metric || ''),
      );
      if (metricCompare !== 0) {
        return metricCompare;
      }

      const sortNum = startDateComparator(a?.metric_start_date, b?.metric_start_date);
      if (sortNum !== 0) {
        return sortNum;
      }

      return endDateComparator(a?.metric_end_date, b?.metric_end_date);
    };
  }

  return (a: any, b: any) => {
    // 其他排序字段也保持 Metrics 分组，确保表格和下载的行顺序一致。
    const metricCompare = String(a?.metric_name || a?.metric || '').localeCompare(
      String(b?.metric_name || b?.metric || ''),
    );
    if (metricCompare !== 0) {
      return metricCompare;
    }

    const aValue = a?.[orderMetric];
    const bValue = b?.[orderMetric];

    // 处理 null/undefined
    if (isNullOrUndefined(aValue) && isNullOrUndefined(bValue)) return 0;
    if (isNullOrUndefined(aValue)) return 1;
    if (isNullOrUndefined(bValue)) return -1;

    // 数字比较
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return isAsc ? aValue - bValue : bValue - aValue;
    }

    // 字符串比较（包括日期字符串）
    const aStr = String(aValue);
    const bStr = String(bValue);
    const compareResult = aStr.localeCompare(bStr);
    return isAsc ? compareResult : -compareResult;
  };
};

/**
 * Alinea 真数始终先按业务指标分组；用户选择的排序字段只影响组内顺序。
 */
export const buildAlineaComparator = (orderInfo: { orderMetric: string; order: string; }) => {
  const { orderMetric, order } = orderInfo;
  const isAsc = order === 'asc';
  const metricOrderMap = ALINEA_CALIBRATION_METRICS.reduce<IRecord<number>>((result, item, index) => {
    return { ...result, [item.value]: index };
  }, {});

  return (a: IRecord, b: IRecord): number => {
    const aMetric = getAlineaMetricItem(a)?.title || a?.metric || '';
    const bMetric = getAlineaMetricItem(b)?.title || b?.metric || '';
    const metricOrder = (metricOrderMap[aMetric] ?? Number.MAX_SAFE_INTEGER) - (metricOrderMap[bMetric] ?? Number.MAX_SAFE_INTEGER);
    if (metricOrder !== 0) {
      return metricOrder;
    }

    const aValue = orderMetric === 'date' ? getAlineaDateValue(a) : a?.[orderMetric];
    const bValue = orderMetric === 'date' ? getAlineaDateValue(b) : b?.[orderMetric];
    const compareResult = String(aValue ?? '').localeCompare(String(bValue ?? ''));
    return isAsc ? compareResult : -compareResult;
  };
};

/**
 * 弹窗分页数据与导出数据共用同一个比较器，避免两条数据链路各自选择排序规则后产生顺序差异。
 */
export const buildTrueDataComparator = (
  mode: TTrueDataMode,
  orderInfo: { orderMetric: string; order: string; },
) => {
  return mode === 'alinea'
    ? buildAlineaComparator(orderInfo)
    : buildSimpleComparator(orderInfo);
};

/**
 * 为连续的相同 Metrics 写入统一的分组标记。
 * 表格排序负责保证同一 Metrics 相邻，列渲染负责隐藏非首行，单元格样式负责移除组内横线。
 */
export const markMetricGroups = <T extends object>(
  records: T[],
  getMetricKey: (record: T) => string,
): Array<T & IMetricGroupFlags> => {
  const groupedRecords = records.map((record) => ({
    ...record,
    is_metric_first: true,
    is_metric_last: true,
  }));

  let groupStartIndex = 0;
  while (groupStartIndex < groupedRecords.length) {
    let groupEndIndex = groupStartIndex + 1;
    while (
      groupEndIndex < groupedRecords.length &&
      getMetricKey(groupedRecords[groupEndIndex]) === getMetricKey(groupedRecords[groupStartIndex])
    ) {
      groupEndIndex += 1;
    }

    let index = groupStartIndex;
    while (index < groupEndIndex) {
      groupedRecords[index].is_metric_first = index === groupStartIndex;
      groupedRecords[index].is_metric_last = index === groupEndIndex - 1;
      index += 1;
    }
    groupStartIndex = groupEndIndex;
  }

  return groupedRecords;
};
